import { createSeededRng } from "../hex-path/seeded-rng";

export type GridAnimationMode = "wipe" | "scale-fade";

export interface GridAnimationBatchItem {
	key: string;
	row: number;
	col: number;
}

export interface ScaleFadeBatchAssignment {
	key: string;
	batchIndex: number;
	startTimeMs: number;
}

export interface HexagonMapAnimationConfig {
	gridAnimationMode?: GridAnimationMode;
	gridAnimationDurationMs?: number;
	gridAnimationTotalDurationMs?: number;
	pathTileDelayMs?: number;
}

export const DEFAULT_PATH_TILE_DELAY_MS = 50;
export const DEFAULT_WIPE_DELAY_MS = DEFAULT_PATH_TILE_DELAY_MS;
export const GRID_ANIMATION_DURATION_MS = 260;
export const GRID_ANIMATION_TOTAL_DURATION_MS = 1000;

export function pathCellRevealDelayMs(
	pathIndex: number,
	config?: HexagonMapAnimationConfig,
): number {
	const delay = config?.pathTileDelayMs ?? DEFAULT_PATH_TILE_DELAY_MS;
	return pathIndex * delay;
}

export function resolveGridAnimationCycleDurationMs(
	config?: HexagonMapAnimationConfig,
): number {
	return Math.max(
		config?.gridAnimationDurationMs ?? GRID_ANIMATION_DURATION_MS,
		config?.gridAnimationTotalDurationMs ??
			GRID_ANIMATION_TOTAL_DURATION_MS,
	);
}

export function resolveGridAnimationStartWindowMs(
	config?: HexagonMapAnimationConfig,
): number {
	return Math.max(
		0,
		resolveGridAnimationCycleDurationMs(config) -
			(config?.gridAnimationDurationMs ?? GRID_ANIMATION_DURATION_MS),
	);
}

export function resolveGridAnimationStepDelayMs(params: {
	stepCount: number;
	config?: HexagonMapAnimationConfig;
}): number {
	const { stepCount, config } = params;
	if (stepCount <= 1) {
		return 0;
	}
	return resolveGridAnimationStartWindowMs(config) / (stepCount - 1);
}

export function resolveGridAnimationStartTimes(params: {
	stepCount: number;
	config?: HexagonMapAnimationConfig;
}): number[] {
	const { stepCount, config } = params;
	const stepDelayMs = resolveGridAnimationStepDelayMs({ stepCount, config });
	return Array.from({ length: stepCount }, (_, index) =>
		Math.round(index * stepDelayMs),
	);
}

export function resolveScaleFadeBatchPlan(params: {
	items: GridAnimationBatchItem[];
	layoutSeed: string;
	layerKey?: string;
	config?: HexagonMapAnimationConfig;
}): {
	batchCount: number;
	batchSize: number;
	startTimesMs: number[];
	assignments: ScaleFadeBatchAssignment[];
} {
	const { items, layoutSeed, layerKey = "grid", config } = params;
	if (items.length === 0) {
		return {
			batchCount: 0,
			batchSize: 0,
			startTimesMs: [],
			assignments: [],
		};
	}
	const batchCount = items.length;
	const startTimesMs = resolveGridAnimationStartTimes({
		stepCount: batchCount,
		config,
	});
	const rng = createSeededRng(`${layoutSeed}:${layerKey}:scale-fade`);
	const orderedItems = [...items];
	for (let index = orderedItems.length - 1; index > 0; index -= 1) {
		const swapIndex = rng.int(index + 1);
		const current = orderedItems[index];
		const swapItem = orderedItems[swapIndex];
		if (!current || !swapItem) {
			continue;
		}
		orderedItems[index] = swapItem;
		orderedItems[swapIndex] = current;
	}
	const assignments: ScaleFadeBatchAssignment[] = orderedItems.map(
		(item, batchIndex) => ({
			key: item.key,
			batchIndex,
			startTimeMs: startTimesMs[batchIndex] ?? 0,
		}),
	);
	return {
		batchCount,
		batchSize: 1,
		startTimesMs,
		assignments,
	};
}

export function resolveWipeStepPlan(params: {
	stepCount: number;
	config?: HexagonMapAnimationConfig;
}): {
	stepCount: number;
	stepDelayMs: number;
	startTimesMs: number[];
} {
	const { stepCount, config } = params;
	const stepDelayMs = resolveGridAnimationStepDelayMs({ stepCount, config });
	return {
		stepCount,
		stepDelayMs,
		startTimesMs: resolveGridAnimationStartTimes({ stepCount, config }),
	};
}

export interface PatchAnimationRequest {
	changedPathIndices: number[];
	mode?: "simultaneous" | "wipe";
	patchTileDelayMs?: number;
}

/** Scaffold for future layout mutations — not triggered in learn v1. */
export function patchAnimationDelays(request: PatchAnimationRequest): number[] {
	const base = request.patchTileDelayMs ?? DEFAULT_WIPE_DELAY_MS;
	if (request.mode === "simultaneous") {
		return request.changedPathIndices.map(() => 0);
	}
	return request.changedPathIndices.map((_, i) => i * base);
}
